/**
 * YoMA mentor panel — the conversational surface in the shell.
 *
 * Presentation only. It reads the sealed Career DNA™ payload to personalise
 * its openers (archetype, top match, growth area) and hands off to /coach with
 * the prompt pre-filled. No AI call, no scoring, no writes happen here.
 *
 * Accessibility: it is a real modal dialog — focus moves in on open, Tab is
 * trapped inside, Escape closes, and focus returns to the trigger.
 */
import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import yomaAvatar from "@/assets/yoma-avatar.png.asset.json";
import { useCareerDna } from "@/hooks/useCareerDna";

const GENERIC_OPENERS = [
  "What should I do next?",
  "Explain my Career DNA™ in simple words",
  "Which careers fit me best, and why?",
  "How do I get started this month?",
] as const;

export default function MentorPanel({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const restoreTo = useRef<HTMLElement | null>(null);
  const { data: dna } = useCareerDna();

  /** Openers built from what the engine already said about this learner. */
  const openers = useMemo<string[]>(() => {
    if (!dna) return [...GENERIC_OPENERS];
    const list: string[] = [];
    if (dna.archetypePrimary) {
      list.push(`What does being a ${dna.archetypePrimary} mean for my next step?`);
    }
    if (dna.matches[0]) {
      list.push(`Why does ${dna.matches[0].name} fit me?`);
    }
    if (dna.matches[1]) {
      list.push(`How do I get into ${dna.matches[1].name} from where I am?`);
    }
    if (dna.topIntelligences[0]?.name) {
      list.push(`How do I use my ${dna.topIntelligences[0].name.toLowerCase()} at work?`);
    }
    for (const g of GENERIC_OPENERS) {
      if (list.length >= 4) break;
      list.push(g);
    }
    return list.slice(0, 4);
  }, [dna]);

  const greeting = dna?.archetypePrimary
    ? `You came out ${dna.archetypePrimary}. Ask me anything about what that means — or start here.`
    : "Tell me what's on your mind — or start with one of these.";

  // Focus management + focus trap.
  useEffect(() => {
    if (!open) return;
    restoreTo.current = document.activeElement as HTMLElement | null;

    const focusables = () =>
      Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ) ?? [],
      ).filter((el) => !el.hasAttribute("disabled"));

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panelRef.current?.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKey);
    // Land on the first opener, not the close button.
    const t = window.setTimeout(() => focusables()[1]?.focus() ?? focusables()[0]?.focus(), 30);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
      restoreTo.current?.focus?.();
    };
  }, [open, onClose]);

  const ask = (q: string) => {
    onClose();
    navigate(`/coach?q=${encodeURIComponent(q)}`);
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-background/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="yoma-panel-title"
            aria-describedby="yoma-panel-greeting"
            className="fixed z-[61] inset-x-3 bottom-3 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[380px] calm-card p-5"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.98 }}
            animate={reduce ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
            transition={reduce ? { duration: 0 } : { type: "spring", stiffness: 320, damping: 30 }}
          >
            <div className="flex items-start gap-3">
              <img
                src={yomaAvatar.url}
                alt=""
                data-testid="yoma-avatar"
                className="h-11 w-11 shrink-0 rounded-full border border-primary/30 object-cover"
              />
              <div className="min-w-0 flex-1">
                <p id="yoma-panel-title" className="font-display text-base font-semibold leading-tight">
                  YoMA
                </p>
                <p className="text-xs text-muted-foreground">Your mentor. Always here.</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close mentor panel"
                className="-mr-1 -mt-1 flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>

            <p
              id="yoma-panel-greeting"
              className="mt-4 rounded-2xl bg-[hsl(var(--surface-2))] p-3.5 text-sm leading-relaxed text-foreground/90"
            >
              {greeting}
            </p>

            <ul className="mt-3 space-y-2" aria-label="Suggested questions">
              {openers.map((q) => (
                <li key={q}>
                  <button
                    type="button"
                    onClick={() => ask(q)}
                    className="flex min-h-11 w-full items-center justify-between gap-3 rounded-2xl border border-border/60 px-3.5 text-left text-sm text-foreground/90 transition-colors hover:border-primary/40 hover:bg-primary/8 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  >
                    <span className="min-w-0">{q}</span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => {
                onClose();
                navigate("/coach");
              }}
              className="mt-4 flex min-h-12 w-full items-center justify-center gap-2 rounded-2xl bg-ai text-sm font-medium text-ai-foreground transition-transform hover:scale-[1.01] active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Open the full conversation
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
